import { auth } from "../firebase/firebase"; 
import { doSignOut, isInDebugMode } from "../firebase/auth";
import { reportErrorToEndpoint } from "./errorReporter";

// Function to update the token in localStorage and in global state/context
const setToken = (newToken) => {
  localStorage.setItem("firebaseToken", newToken); // Update in localStorage
  // You can also update your global state (context or state management) here if needed
};



export const makeAuthenticatedRequest = async (url, options = {}) => {
  let user = auth.currentUser;

  // Recover session if user is null
  if (!user) {
    console.warn("User is null. Attempting to recover session...");
    await new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        if (authUser) {
          user = authUser;
          unsubscribe();
          resolve();
        }
      });
    });
  }

  if (!user) {
    console.error("Session recovery failed. User is not authenticated.");
    throw new Error("User is not authenticated");
  }

  let authToken = localStorage.getItem("firebaseToken");

  // Function to check token expiration
  const isTokenExpired = (token) => {
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      return Date.now() >= decodedToken.exp * 1000; // Convert expiration time to milliseconds
    } catch (error) {
      console.error("Token validation error:", error);
      return true; // Assume expired if there's an error
    }
  };

  // Refresh token if expired or missing
  if (!authToken || isTokenExpired(authToken)) {
    console.log("Token expired or missing, refreshing...");

    try {
      authToken = await user.getIdToken(true); // Get a fresh token
      setToken(authToken); // Update both the localStorage and global state (if needed)

      console.log("New token obtained:", authToken);
    } catch (error) {
      console.error("Failed to refresh token:", error);
      handleLogout();
      throw new Error("Failed to refresh token");
    }
  }

  console.log("Using Token:", authToken);

  const headers = {
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
    ...options.headers,
  };

  try {
    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("Unauthorized. Attempting token refresh...");
        try {
          // Refresh token and retry the request
          authToken = await user.getIdToken(true); // Get fresh token
          setToken(authToken); // Update the token

          const retryHeaders = {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            ...options.headers,
          };

          const retryResponse = await fetch(url, {
            ...options,
            headers: retryHeaders,
          });

          if (!retryResponse.ok) {
            throw new Error(`Retry API Request Failed: ${retryResponse.status}`);
          }

          return retryResponse; // Return successful retry response
        } catch (refreshError) {
          console.error("Token refresh failed on retry:", refreshError);
          handleLogout();
          throw new Error("Failed to refresh token on retry");
        }
      }

      const errorDetails = await response.text();
      console.error("API Response Error Details:", errorDetails);
      
      // If in debug mode (test1@gmail.com), show detailed error
      if (isInDebugMode()) {
        let errorMessage = "API Error: " + response.status + " " + response.statusText;
        try {
          // Try to parse as JSON to get more details
          const errorJson = JSON.parse(errorDetails);
          if (errorJson.error) {
            errorMessage += "\n\nDetails: " + (errorJson.error.message || JSON.stringify(errorJson.error));
          } else if (errorJson.message) {
            errorMessage += "\n\nDetails: " + errorJson.message;
          } else {
            errorMessage += "\n\nDetails: " + JSON.stringify(errorJson);
          }
        } catch (e) {
          // If not JSON, just show the raw error
          errorMessage += "\n\nDetails: " + errorDetails;
        }
        throw new Error(errorMessage);
      }
      
      throw new Error("Something went wrong. Please try again later.");
    }

    return response;
  } catch (error) {
    console.error("API Request Error:", error);

    // --- Error reporting block ---
    let itinerary_id = "";
    let itinerary_json = {};
    try {
      if (options.body) {
        const bodyObj = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
        if (bodyObj.itinerary_id) itinerary_id = bodyObj.itinerary_id;
        if (bodyObj.itinerary_json) itinerary_json = bodyObj.itinerary_json;
      }
    } catch (e) {}

    let user_data = {};
    try {
      // Try to get user info from localStorage or window (customize as needed)
      const storedUser = window.user || JSON.parse(localStorage.getItem('user')) || {};
      user_data = {
        email: storedUser.email || '',
        uid: storedUser.uid || '',
        user: storedUser.name || ''
      };
    } catch (e) { user_data = {}; }

    reportErrorToEndpoint({
      error_message: error.message || 'Unknown error',
      id: 'api-request-error',
      itinerary_id,
      itinerary_json,
      endpoint: url,
      user_data
    });
    // --- End error reporting block ---

    // If in debug mode (test1@gmail.com), show detailed error
    if (isInDebugMode()) {
      let errorMessage = "API Request Error: " + error.message;
      errorMessage += "\n\nEndpoint: " + url;
      
      // Add request details if available
      if (options.method) {
        errorMessage += "\nMethod: " + options.method;
      }
      
      if (options.body) {
        try {
          const bodyStr = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
          errorMessage += "\n\nRequest Body: " + bodyStr;
        } catch (e) {}
      }
      
      alert(errorMessage);
    } else {
      alert("Something went wrong. Please try again later.");
    }
    
    return Promise.reject(error);
  }
};

// Logout function in case of token issues
const handleLogout = async () => {
  console.warn("Logging out the user...");
  await doSignOut();
  localStorage.removeItem("firebaseToken"); // Remove token on logout
  window.location.href = "/login"; // Redirect to login page
};
